import { Api } from "../Api";
import { AsyncLock } from "../AsyncLock";
import { DependencyContainer } from "../DependencyContainer";
import { Event, IEvent } from "../Event";
import { ITypedEvent, TypedEvent } from "../TypedEvent";
import { AppState, IAppStateService } from "./IAppStateService";
import { ConnectionState, IExalusConnectionService } from "./IExalusConnectionService";
import { ISessionService } from "./Session/ISessionService";

export class AppStateService implements IAppStateService {
	//// BLOCK DISCONNECTION WHEN PENDING REQUESTS
	private _isConnectedAndAuthorized: boolean = false;
	private _syncLock: AsyncLock = new AsyncLock();
	private _isInFouces: boolean = true;
	private _lastVisibilityState: boolean | null = null;
	private _canHibernate: boolean = true;
	private _session: ISessionService | null = null;
	private _connection: IExalusConnectionService | null = null;
	private _currentAppState: AppState = AppState.Disconnected;
	private _returnedFromSuspension: boolean = false;
	public static readonly ServiceName: string = "AppStateService";
	private _onReturnedFromSuspensionEvent: Event = new Event();
	private _onSuspendedEvent: Event = new Event();
	private _onEnterLowPowerModeEvent: Event = new Event();
	private _onExitLowPowerModeEvent: Event = new Event();
	private _onBackButtonPressedEvent: Event = new Event();
	private _onForwardButtonPressedEvent: Event = new Event();
	private _onAppStateChanged: TypedEvent<AppState> = new TypedEvent<AppState>();
	static AppState: any;

	public constructor() {
		this.MonitorAppState();
		this._onAppStateChanged.Subscribe((state: AppState) => this._currentAppState = state);
		this._lastVisibilityState = this.IsHidden();

		if (navigator.userAgent.match(/Safari/i) != null && !navigator.mediaDevices) {
			const onBlur = () => {
				if (!this._isInFouces)
					return;

				this._isInFouces = false;
				this.Suspend();
			};

			const onFocus = () => {
				if (this._isInFouces)
					return;

				this._isInFouces = true;
				this.ReturnFromSuspension();
			};

			// Support both standard DOM events and legacy/polyfilled aliases used by some WebView runtimes.
			window.addEventListener("blur", onBlur);
			window.addEventListener("onblur", onBlur);
			window.addEventListener("focus", onFocus);
			window.addEventListener("onfocus", onFocus);
		}
		else {
			const onVisibilityChanged = async () => {
				await this._syncLock.WaitForLockAsync();
				this._syncLock.Lock();

				try {
					const isHidden = this.IsHidden();
					if (this._lastVisibilityState === isHidden)
						return;

					this._lastVisibilityState = isHidden;

					if (isHidden)
						this.Suspend();
					else
						this.ReturnFromSuspension();
				}
				finally {
					this._syncLock.Unlock();
				}
			};

			// Some browsers/runtime shells surface this through document, others also forward it to window.
			document.addEventListener("visibilitychange", onVisibilityChanged);
			window.addEventListener("visibilitychange", onVisibilityChanged);
		}

		window.addEventListener("popstate", (event: PopStateEvent) => {
			var loc = document.location;
			if (event.state) {
				// state is defined
				if (event.state.direction == "forward") {
					DependencyContainer.Log?.Warning(AppStateService.ServiceName, "Forward button pressed");
					this._onForwardButtonPressedEvent?.Invoke();
				}
				else {
					DependencyContainer.Log?.Warning(AppStateService.ServiceName, "Back button pressed");
					this._onBackButtonPressedEvent?.Invoke();
				}
			}
		});
	}

	private async MonitorAppState(): Promise<void> {
		this._session = await Api.GetAsync<ISessionService>("SessionService");
		this._connection = await Api.GetAsync<IExalusConnectionService>("ExalusConnectionService");
		this._session.OnUserLoggedInEvent().Subscribe((user) => {
			DependencyContainer.Log?.Warning(AppStateService.ServiceName, `App state changed to: logged in`);
			this._onAppStateChanged.Invoke(AppState.LoggedIn);
		});

		this._session.OnUserLoggedOutEvent().Subscribe((user) => {
			DependencyContainer.Log?.Warning(AppStateService.ServiceName, `App state changed to: logged out`);
			this._onAppStateChanged.Invoke(AppState.LoggedOut);
		});

		this._connection.OnConnectionStateChangedEvent().Subscribe((state) => {
			switch (state) {
				case ConnectionState.ConnectedAndAuthorized:
					DependencyContainer.Log?.Warning(AppStateService.ServiceName, `App state changed to: connected`);
					this._isConnectedAndAuthorized = true;
					this._onAppStateChanged.Invoke(AppState.Connected);
					break;

				case ConnectionState.Disconnected:
					DependencyContainer.Log?.Warning(AppStateService.ServiceName, `App state changed to: disconnected`);

					if (this._isConnectedAndAuthorized)
						this._onAppStateChanged.Invoke(AppState.Disconnected);

					this._isConnectedAndAuthorized = false;
					break;

				case ConnectionState.Connecting:
					DependencyContainer.Log?.Warning(AppStateService.ServiceName, `App state changed to: connecting`);
					this._onAppStateChanged.Invoke(AppState.Connecting);
					break;

				case ConnectionState.Disconnecting:
					DependencyContainer.Log?.Warning(AppStateService.ServiceName, `App state changed to: disconnecting`);
					this._onAppStateChanged.Invoke(AppState.Disconnecting);

					break;

				case ConnectionState.Reconnecting:
					DependencyContainer.Log?.Warning(AppStateService.ServiceName, `App state changed to: reconnecting`);
					this._onAppStateChanged.Invoke(AppState.Reconnecting);
					break;

				case ConnectionState.Failed:
					DependencyContainer.Log?.Warning(AppStateService.ServiceName, `App state changed to: failed`);
					this._onAppStateChanged.Invoke(AppState.FailedToConnect);
					break;
			}
		});
	}

	get CurrentAppState(): AppState {
		return this._currentAppState;
	}

	OnAppStateChanged(): ITypedEvent<AppState> {
		return this._onAppStateChanged;
	}

	Suspend(): void {
		if (!this.IsHibernationAllowed())
			return;

		this._returnedFromSuspension = false;
		this._onSuspendedEvent.Invoke();
		this._onAppStateChanged.Invoke(AppState.Suspended);

		DependencyContainer.Log?.Warning(AppStateService.ServiceName, `App state changed to: suspended`);
		DependencyContainer.Log?.Warning(AppStateService.ServiceName, "App switched to background");
	}

	ReturnFromSuspension(): void {
		if (!this.IsHibernationAllowed())
			return;

		this._returnedFromSuspension = true;
		this._onReturnedFromSuspensionEvent.Invoke();
		this._onAppStateChanged.Invoke(AppState.ReturnedFromSuspension);

		DependencyContainer.Log?.Warning(AppStateService.ServiceName, `App state changed to: returned from suspension`);
		DependencyContainer.Log?.Warning(AppStateService.ServiceName, "App switched to foreground");
	}

	EnterLowPowerMode(): void {
		this._onEnterLowPowerModeEvent.Invoke();
		this._onAppStateChanged.Invoke(AppState.EnteredLowPowerMode);
	}

	ExitLowPowerMode(): void {
		this._onExitLowPowerModeEvent.Invoke();
		this._onAppStateChanged.Invoke(AppState.ExitedLowPowerMode);
	}

	IsWorkingInWebView(): boolean {
		/// for all if index file is hosted locally
		/// for Android WebView
		if (window.location.protocol === "file:")
			return true;

		/// for apple devices
		if (navigator.userAgent.match(/Safari/i) != null) {
			/// normally iOS WebView does not support mediaDevices like in Safari or other browsers
			if (navigator.mediaDevices)
				return false;
			else
				return true;
		}
		else {
			// is not android web view
			return false;
		}
	}

	private IsHidden(): boolean {
		return document.hidden;
	}

	IsActive(): boolean {
		return !this.IsHidden();
	}

	IsInBackground(): boolean {
		return this.IsHidden();
	}

	ReturnedFromSuspension(): boolean {
		return this._returnedFromSuspension;
	}

	IsHibernationAllowed(): boolean {
		return this._canHibernate;
	}

	DisallowHibernation(): void {
		this._canHibernate = false;
	}

	AllowHibernation(): void {
		this._canHibernate = true;
	}

	OnReturnedFromSuspension(): IEvent {
		return this._onReturnedFromSuspensionEvent;
	}

	OnSuspended(): IEvent {
		return this._onSuspendedEvent;
	}

	OnEnterLowPowerMode(): IEvent {
		return this._onEnterLowPowerModeEvent;
	}

	OnExitLowPowerMode(): IEvent {
		return this._onExitLowPowerModeEvent;
	}

	OnBackButtonPressed(): IEvent {
		return this._onBackButtonPressedEvent;
	}

	OnForwardButtonPressed(): IEvent {
		return this._onForwardButtonPressedEvent;
	}

	GetServiceName(): string {
		return AppStateService.ServiceName;
	}
}
